import { createContext, useContext, useCallback, useState, type ReactNode } from 'react';

import { useLocalStorage } from '@/shared/hooks/useLocalStorage';
import { t } from '@/i18n';

import type { Exchange } from '@/entities/market/types/exchange';

export interface WatchList {
  id: string;
  name: string;
  exchange: Exchange;
  markets: string[];
}

interface InterestContextType {
  watchLists: WatchList[];
  activeListId: string;
  setActiveListId: (id: string) => void;
  activeList: WatchList;
  addWatchList: (name: string, exchange: Exchange) => void;
  removeWatchList: (id: string) => void;
  renameWatchList: (id: string, name: string) => void;
  toggleFavorite: (market: string) => void;
  isFavorite: (market: string) => boolean;
}

const InterestContext = createContext<InterestContextType | undefined>(undefined);

const STORAGE_KEY = 'interest-watchlists';
// 🔴 TRAD-2099 (W-18) — `t()` 는 함수 몸체 안에서만 호출한다(module-scope-t-call-guard).
// 기존엔 모듈 스코프 상수라 하드코딩된 '관심 종목' 리터럴이었다. market.tsx가 같은 개념에
// 이미 쓰는 `crypto_market.watchlist.default_name`(KO "관심 종목"/EN "Watchlist")을 그대로
// 재사용한다 — 새 카피 없음.
const getDefaultList = (): WatchList => ({
  id: 'default',
  name: t('crypto_market.watchlist.default_name'),
  exchange: 'UPBIT',
  markets: [],
});

function migrateOldData(): WatchList[] | null {
  const old = localStorage.getItem('interest-markets');
  if (!old) return null;
  const markets = JSON.parse(old) as string[];
  localStorage.removeItem('interest-markets');
  if (markets.length === 0) return null;
  return [{ id: 'default', name: t('crypto_market.watchlist.default_name'), exchange: 'UPBIT', markets }];
}

function migrateExchange(lists: WatchList[]): WatchList[] {
  return lists.map(l => (l.exchange ? l : { ...l, exchange: 'UPBIT' as Exchange }));
}

export const InterestProvider = ({ children }: { children: ReactNode }) => {
  const [watchLists, setWatchLists] = useLocalStorage<WatchList[]>(STORAGE_KEY, () => {
    const migrated = migrateOldData();
    return migrated ? migrateExchange(migrated) : [getDefaultList()];
  });

  // 기존 데이터에 exchange가 없는 경우 마이그레이션
  if (watchLists.some(l => !l.exchange)) {
    setWatchLists(migrateExchange(watchLists));
  }
  const [activeListId, setActiveListId] = useState(watchLists[0]?.id ?? 'default');

  const activeList = watchLists.find(l => l.id === activeListId) ?? watchLists[0] ?? getDefaultList();

  const addWatchList = useCallback(
    (name: string, exchange: Exchange) => {
      const newList: WatchList = { id: crypto.randomUUID(), name, exchange, markets: [] };
      setWatchLists(prev => [...prev, newList]);
      setActiveListId(newList.id);
    },
    [setWatchLists],
  );

  const removeWatchList = useCallback(
    (id: string) => {
      setWatchLists(prev => {
        if (prev.length <= 1) return prev;
        const next = prev.filter(l => l.id !== id);
        if (activeListId === id) setActiveListId(next[0].id);
        return next;
      });
    },
    [setWatchLists, activeListId],
  );

  const renameWatchList = useCallback(
    (id: string, name: string) => {
      setWatchLists(prev => prev.map(l => (l.id === id ? { ...l, name } : l)));
    },
    [setWatchLists],
  );

  const toggleFavorite = useCallback(
    (market: string) => {
      setWatchLists(prev =>
        prev.map(l => {
          if (l.id !== activeListId) return l;
          const markets = l.markets.includes(market) ? l.markets.filter(m => m !== market) : [...l.markets, market];
          return { ...l, markets };
        }),
      );
    },
    [setWatchLists, activeListId],
  );

  const isFavorite = useCallback((market: string) => activeList.markets.includes(market), [activeList]);

  return (
    <InterestContext.Provider
      value={{
        watchLists,
        activeListId,
        setActiveListId,
        activeList,
        addWatchList,
        removeWatchList,
        renameWatchList,
        toggleFavorite,
        isFavorite,
      }}
    >
      {children}
    </InterestContext.Provider>
  );
};

export const useInterest = (): InterestContextType => {
  const context = useContext(InterestContext);
  if (!context) {
    throw new Error('useInterest must be used within an InterestProvider');
  }
  return context;
};
